import { useMemo, useState, type ReactNode } from 'react';
import { T, useGT, Var } from 'gt-react';
import { CheckIcon, PencilSimpleIcon } from '@phosphor-icons/react';
import {
  EMPTY_REPORT_CANVAS_LAYOUT,
  REPORT_CANVAS_COLUMNS,
  ReportCanvasGridLayoutSchema,
  type ReportCanvasLayout,
  type ReportFilter,
} from '@erp/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ReportCanvas } from '@/components/report-canvas/ReportCanvas';
import { ReportCanvasProvider } from '@/components/report-canvas/ReportCanvasProvider';
import { ReportCanvasView } from '@/components/report-canvas/ReportCanvasView';
import {
  ReportDataContext,
  type ReportDataContextValue,
} from '@/components/report-canvas/ReportDataContext';
import {
  ReportCanvasSidebar,
  type SidebarSelection,
} from '@/components/report-canvas/ReportCanvasSidebar';
import { componentSize } from '@/components/report-canvas/registry';
import type { LayoutUpdate } from '@/components/report-canvas/useReportCanvasDnd';
import { ComponentRegistryContext } from '@/components/report-canvas/registry-context';
import { CanvasPromptBar } from '@/components/report-canvas/CanvasPromptBar';
import {
  useCanvasGeneration,
  useComponentGeneration,
} from '@/components/report-canvas/useCanvasGeneration';
import { hydrateLayout } from '@/components/report-canvas/hydrate';
import {
  addComponent,
  removeItem,
  resizeItem,
  updateItem,
} from '@/components/report-canvas/layout';
import {
  deserializeLayout,
  serializeLayout,
} from '@/components/report-canvas/serialization';
import type { GridLayout } from '@/components/report-canvas/types';
import {
  reportLayoutDatasetKeys,
  type WorkbookReportData,
  type WorkbookReportSource,
} from './useWorkbookReportData';
import { workbookReportRegistry } from './workbookReportRegistry';
import { useWorkbookRunTime } from './workbookRunTime';
import { UndoHistoryControls } from '@/components/common/UndoHistoryControls';
import { describeLayoutChange } from '@/components/report-canvas/layoutChange';
import { useLayoutHistoryStep } from '@/components/report-canvas/useLayoutHistoryStep';
import { useUndoHotkeys, useUndoTree } from '@/lib/useUndoTree';
import {
  WorkbookReportHeading,
  type WorkbookReportHeadingValue,
} from './WorkbookReportHeading';
import { ReportColorPaletteSelect } from '@/components/report-canvas/palette/ReportColorPaletteSelect';
import {
  ReportFilterBar,
  ReportValuesControlContext,
} from '@/components/report-canvas/filters/ReportFilterBar';
import { ReportFilterSettings } from '@/components/report-canvas/filters/ReportFilterSettings';
import {
  resolveReportFilters,
  useFilteredDatasets,
  WorkbookReportScope,
} from './workbookReportScope';
import { WorkbookValuesControl } from './WorkbookReportValuesControl';
import {
  changedReportParts,
  reportContent,
  sameReportContent,
  type WorkbookReportOptions,
  unrepeatedHeading,
} from './workbookReportContent';

export type { WorkbookReportOptions } from './workbookReportContent';

/** The Report datasets a layout charts, as their sources. */
export function chartedSources(
  layout: ReportCanvasLayout | undefined,
  data: Pick<WorkbookReportData, 'sources' | 'defaultDatasetKey'>,
): WorkbookReportSource[] {
  return [...reportLayoutDatasetKeys(layout, data.defaultDatasetKey)].flatMap(
    (key) => {
      const source = data.sources.get(key);
      return source ? [source] : [];
    },
  );
}

/** Whether the charted Results are there to see, and Current. */
function ReportStatus({
  sources,
  definition,
}: {
  sources: readonly WorkbookReportSource[];
  definition: Parameters<typeof useWorkbookRunTime>[0];
}) {
  const gt = useGT();
  const runTime = useWorkbookRunTime(definition);
  if (!sources.length) return null;
  if (sources.every((source) => source.path === 'none'))
    return (
      <p role="status" className="text-xs text-muted-foreground">
        <T context="workbook report never run">
          Run the workbook to see this report.
        </T>
      </p>
    );
  const stale = sources.filter(
    (source) => source.path !== 'none' && !source.current,
  );
  if (!stale.length) return null;
  const ranAt = stale[0]?.ranAt;
  return (
    // A div: Badge renders one, which a <p> can't hold.
    <div
      role="status"
      className="flex items-center gap-2 text-xs text-muted-foreground"
    >
      <Badge variant="outline" title={ranAt ? runTime.full(ranAt) : undefined}>
        {gt('Stale', { context: 'workbook report stale badge' })}
      </Badge>
      <span>
        <T context="workbook report stale">
          <Var>{stale.map((source) => source.label).join(', ')}</Var> changed
          since the run these charts show. Run the workbook again to update
          them.
        </T>
      </span>
    </div>
  );
}

const SIDEBAR_COLLAPSED_KEY = 'workbooks.report.sidebarCollapsed';

/** Whether this viewer last collapsed the Report Builder sidebar; open when unknown. */
function readSidebarCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}
function writeSidebarCollapsed(collapsed: boolean) {
  try {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  } catch {
    // Storage can be unavailable (private mode, blocked site data); the sidebar still works.
  }
}

/** The selected item with its size on the grid, or null once it is gone. */
function findSelection(
  layout: GridLayout,
  itemId: string | null,
): SidebarSelection | null {
  const item = layout.find((entry) => entry.id === itemId);
  if (!item) return null;
  const { minW, minH } = componentSize(
    workbookReportRegistry,
    item.component.type,
  );
  return {
    component: item.component,
    w: item.w,
    h: item.h,
    maxW: REPORT_CANVAS_COLUMNS - item.x,
    minW,
    minH,
  };
}

/**
 * The Report laid out in place: the canvas on the grid, and the Report
 * Builder sidebar — the palette, or the selected component's settings and its
 * own AI prompt.
 */
function ReportEditor({
  layout,
  change,
  isAnyDragging,
  isGenerating,
  data,
  reportSettings,
}: {
  layout: GridLayout;
  change: LayoutUpdate;
  isAnyDragging: boolean;
  isGenerating: boolean;
  data: Pick<ReportDataContextValue, 'datasets' | 'defaultDatasetKey'>;
  /** The whole Report's settings, in the sidebar while no component is selected. */
  reportSettings?: ReactNode;
}) {
  const registry = workbookReportRegistry;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);
  const componentGeneration = useComponentGeneration({
    registry,
    datasets: data.datasets,
    defaultDatasetKey: data.defaultDatasetKey,
  });
  const selected = findSelection(layout, selectedId);
  const busy = isGenerating || componentGeneration.isGenerating;
  const add = (type: string) => {
    const added = addComponent(layout, registry, type);
    if (!added.id) return;
    change(() => added.layout);
    setSelectedId(added.id);
  };
  return (
    <div className="flex min-h-[26rem]">
      <div
        className={`min-w-0 flex-1 ${busy ? 'pointer-events-none opacity-60' : ''}`}
        aria-busy={busy}
      >
        <ReportCanvas
          layout={layout}
          isAnyDragging={isAnyDragging}
          onDelete={(id) => change((previous) => removeItem(previous, id))}
          onUpdate={(id, component) =>
            change((previous) => updateItem(previous, id, component))
          }
          onArrange={change}
          onAdd={add}
          selection={{
            itemId: selected ? selectedId : null,
            onSelect: (id) => {
              setSelectedId(id);
              if (collapsed) {
                setCollapsed(false);
                writeSidebarCollapsed(false);
              }
            },
          }}
        />
      </div>
      <ReportCanvasSidebar
        collapsed={collapsed}
        onCollapsedChange={(next) => {
          setCollapsed(next);
          writeSidebarCollapsed(next);
        }}
        selection={selected}
        onDeselect={() => setSelectedId(null)}
        onUpdate={(component) => {
          if (!selected) return;
          change((previous) => updateItem(previous, component.id, component));
        }}
        onDelete={() => {
          if (!selected) return;
          change((previous) => removeItem(previous, selected.component.id));
          setSelectedId(null);
        }}
        onSizeChange={(size) => {
          if (!selected) return;
          const id = selected.component.id;
          change((previous) => resizeItem(previous, id, size, selected), id);
        }}
        onAdd={add}
        generation={componentGeneration}
        reportSettings={reportSettings}
      />
    </div>
  );
}

const NO_FILTERS: ReportFilter[] = [];

/**
 * The saved layout on the grid, and a draft of it while editing: the draft
 * keeps its drag-and-drop ids between changes, and the layout it last saved,
 * to tell a change made elsewhere (then it restarts from the saved one).
 */
function useReportDraft(layout: ReportCanvasLayout | undefined) {
  const registry = workbookReportRegistry;
  const serialized = layout ?? EMPTY_REPORT_CANVAS_LAYOUT;
  // Saved component ids are kept, so a component stays selected when the
  // layout comes back changed from outside the editor (an undo).
  const saved = useMemo(
    () =>
      hydrateLayout(
        deserializeLayout(serialized, registry, { keepComponentIds: true }),
        registry,
      ),
    [serialized, registry],
  );
  const savedKey = JSON.stringify(serialized);
  const [draft, setDraft] = useState<{
    layout: GridLayout;
    key: string;
  } | null>(null);
  if (draft && draft.key !== savedKey)
    setDraft({ layout: saved, key: savedKey });
  return { saved, savedKey, draft, setDraft };
}

/** A viewer's own filter values while viewing, until the saved filters change; editors see the saved ones. */
function useViewerFilters(savedFilters: ReportFilter[], editing: boolean) {
  const [viewing, setViewing] = useState<{
    key: string;
    filters: ReportFilter[];
  } | null>(null);
  const savedFiltersKey = JSON.stringify(savedFilters);
  const shownFilters =
    !editing && viewing?.key === savedFiltersKey
      ? viewing.filters
      : savedFilters;
  const view = (filters: ReportFilter[]) =>
    setViewing({ key: savedFiltersKey, filters });
  return { shownFilters, view };
}

/**
 * A Report: a canvas of charts over Report datasets, read-only for viewers
 * and laid out in place for editors (`onChange`). Each change is saved as it
 * is made.
 */
export function WorkbookReportCanvas({
  layout,
  onChange,
  heading,
  onHeadingChange,
  data,
  definition,
  actions,
  active = true,
  options,
  onOptionsChange,
}: {
  layout: ReportCanvasLayout | undefined;
  /** Absent for viewers who cannot edit the Report. */
  onChange?: (layout: ReportCanvasLayout) => void;
  /** The title and description above the grid; absent = the Report has none. */
  heading?: WorkbookReportHeadingValue;
  /** Edits the heading while the Report is being laid out. */
  onHeadingChange?: (
    heading: WorkbookReportHeadingValue,
    field: keyof WorkbookReportHeadingValue,
  ) => void;
  data: WorkbookReportData;
  definition: Parameters<typeof useWorkbookRunTime>[0];
  /** More header actions, shown beside Edit Report. */
  actions?: ReactNode;
  /**
   * Whether the Report is the surface in view. Only then, and while it is
   * laid out, do Cmd/Ctrl+Z and redo move its history instead of the
   * enclosing editor's.
   */
  active?: boolean;
  /** Its color palette and filters; absent = the organization's default palette, no filters. */
  options?: WorkbookReportOptions;
  /** Saves them while the Report is being laid out (and each filter value an editor sets). */
  onOptionsChange?: (options: WorkbookReportOptions) => void;
}) {
  const gt = useGT();
  const registry = workbookReportRegistry;
  const { saved, savedKey, draft, setDraft } = useReportDraft(layout);
  const editing = draft !== null && !!onChange;
  const content = reportContent(layout, heading, options);
  const history = useUndoTree({
    value: content,
    equals: sameReportContent,
    apply: (state) => {
      const changed = changedReportParts(state, content);
      if (changed.layout) onChange?.(state.layout);
      if (changed.heading)
        onHeadingChange?.(
          { title: state.title, description: state.description },
          'title',
        );
      if (changed.options)
        onOptionsChange?.({
          paletteId: state.paletteId,
          filters: state.filters,
        });
    },
    openedLabel: gt('Opened', { context: 'report edit history first step' }),
    elsewhereLabel: gt('Changed elsewhere', {
      context: 'report edit history step made outside the report editor',
    }),
  });
  useUndoHotkeys(history, editing && active);
  const stepFor = useLayoutHistoryStep(workbookReportRegistry);
  const change: LayoutUpdate = (updater, actedOn) => {
    const previous = draft?.layout ?? saved;
    const next = updater(previous);
    const serialized = ReportCanvasGridLayoutSchema.parse(
      serializeLayout(next),
    );
    const before = ReportCanvasGridLayoutSchema.parse(
      serializeLayout(previous),
    );
    // A drop back where the item was, or a resize to its own size.
    if (JSON.stringify(serialized) === JSON.stringify(before)) return;
    setDraft({ layout: next, key: JSON.stringify(serialized) });
    history.record(
      { ...content, layout: serialized },
      stepFor(describeLayoutChange(before, serialized, actedOn)),
    );
    onChange?.(serialized);
  };
  function changeHeading(
    next: WorkbookReportHeadingValue,
    field: keyof WorkbookReportHeadingValue,
  ) {
    history.record(
      { ...content, title: next.title, description: next.description },
      {
        label:
          field === 'title'
            ? gt('Edited the title', { context: 'report edit history step' })
            : gt('Edited the description', {
                context: 'report edit history step',
              }),
        group: field,
      },
    );
    onHeadingChange?.(next, field);
  }
  function changeOptions(
    next: WorkbookReportOptions,
    label: string,
    group: string,
  ) {
    history.record({ ...content, ...next }, { label, group });
    onOptionsChange?.(next);
  }
  const savedFilters = options?.filters ?? NO_FILTERS;
  const { shownFilters, view } = useViewerFilters(savedFilters, editing);
  const setShownFilters = (next: ReportFilter[]) => {
    if (editing)
      changeOptions(
        { ...options, filters: next },
        gt('Changed a filter', { context: 'report edit history step' }),
        'filter-values',
      );
    else view(next);
  };
  const today = new Date().toDateString();
  const resolvedFilters = useMemo(
    () => resolveReportFilters(shownFilters, new Date(today)),
    [shownFilters, today],
  );
  const filteredData = useFilteredDatasets(data.datasets, resolvedFilters);
  const generation = useCanvasGeneration({
    layout: draft?.layout ?? null,
    registry,
    datasets: data.datasets,
    defaultDatasetKey: data.defaultDatasetKey,
    onApply: (generated) => change(() => generated),
  });
  const shown = editing ? draft.layout : saved;
  const sources = chartedSources(
    editing
      ? ReportCanvasGridLayoutSchema.parse(serializeLayout(draft.layout))
      : layout,
    data,
  );
  const neverRun = sources.every((source) => source.path === 'none');
  const dataValue = useMemo(
    () => ({
      datasets: filteredData,
      defaultDatasetKey: data.defaultDatasetKey,
      emptyHint: neverRun
        ? gt('Run the workbook to see this report.', {
            context: 'workbook report never run',
          })
        : undefined,
    }),
    [filteredData, data.defaultDatasetKey, neverRun, gt],
  );
  // The filter bar and Filters list read every row, not the filtered ones.
  const unfilteredValue = useMemo(
    () => ({
      datasets: data.datasets,
      defaultDatasetKey: data.defaultDatasetKey,
    }),
    [data.datasets, data.defaultDatasetKey],
  );
  const reportSettings = (
    <WorkbookReportSettings
      options={options}
      filters={savedFilters}
      data={unfilteredValue}
      changeOptions={changeOptions}
    />
  );
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ReportStatus sources={sources} definition={definition} />
        <div className="ml-auto flex items-center gap-1.5">
          {actions}
          {editing && (
            <UndoHistoryControls
              controls={history}
              label={gt('Report edit history', {
                context: 'report undo history controls',
              })}
            />
          )}
          {onChange && (
            <ReportEditToggle
              editing={editing}
              disabled={generation.isGenerating}
              onDone={() => {
                setDraft(null);
                generation.reset();
              }}
              onEdit={() => setDraft({ layout: saved, key: savedKey })}
            />
          )}
        </div>
      </div>
      {heading && (
        <WorkbookReportHeading
          heading={editing ? heading : unrepeatedHeading(heading, layout)}
          onChange={editing && onHeadingChange ? changeHeading : undefined}
        />
      )}
      <WorkbookReportScope
        data={data}
        paletteId={options?.paletteId}
        filters={resolvedFilters}
      >
        <ReportValuesControlContext.Provider value={WorkbookValuesControl}>
          <ReportDataContext.Provider value={unfilteredValue}>
            <ReportFilterBar
              filters={shownFilters}
              onChange={setShownFilters}
            />
          </ReportDataContext.Provider>
          {editing ? (
            <div className="rounded-lg border">
              <CanvasPromptBar
                controls={generation}
                placeholder={gt(
                  'Describe the report — e.g. "net by customer as a bar chart, and cases by month as a line"',
                  { context: 'workbook report prompt placeholder' },
                )}
              />
              <ReportCanvasProvider
                registry={registry}
                datasets={dataValue.datasets}
                defaultDatasetKey={dataValue.defaultDatasetKey}
                emptyHint={dataValue.emptyHint}
                onLayoutChange={change}
              >
                {({ isAnyDragging }) => (
                  <ReportEditor
                    layout={shown}
                    change={change}
                    isAnyDragging={isAnyDragging}
                    isGenerating={generation.isGenerating}
                    data={dataValue}
                    reportSettings={reportSettings}
                  />
                )}
              </ReportCanvasProvider>
            </div>
          ) : (
            <ReportCanvasViewOrEmpty
              layout={shown}
              data={dataValue}
              canEdit={!!onChange}
            />
          )}
        </ReportValuesControlContext.Provider>
      </WorkbookReportScope>
    </section>
  );
}

/** Edit report, or Done while the Report is being laid out. */
function ReportEditToggle({
  editing,
  disabled,
  onDone,
  onEdit,
}: {
  editing: boolean;
  disabled: boolean;
  onDone: () => void;
  onEdit: () => void;
}) {
  if (editing)
    return (
      <Button
        size="sm"
        className="h-7 gap-1 text-xs"
        disabled={disabled}
        onClick={onDone}
      >
        <CheckIcon className="h-4 w-4" />
        <T context="workbook report finish editing">Done</T>
      </Button>
    );
  return (
    <Button
      variant="outline"
      size="sm"
      className="h-7 gap-1 text-xs"
      onClick={onEdit}
    >
      <PencilSimpleIcon className="h-4 w-4" />
      <T context="workbook report edit">Edit report</T>
    </Button>
  );
}

/** The Report as viewers see it, or a note that it charts nothing yet. */
function ReportCanvasViewOrEmpty({
  layout,
  data,
  canEdit,
}: {
  layout: GridLayout;
  data: ReportDataContextValue;
  canEdit: boolean;
}) {
  if (layout.length)
    return (
      <ComponentRegistryContext.Provider value={workbookReportRegistry}>
        <ReportDataContext.Provider value={data}>
          <ReportCanvasView layout={layout} />
        </ReportDataContext.Provider>
      </ComponentRegistryContext.Provider>
    );
  return (
    <div className="flex h-40 flex-col items-center justify-center gap-1 rounded-lg border bg-muted/30 text-sm text-muted-foreground">
      {canEdit ? (
        <T context="workbook report empty for editors">
          No charts yet. Edit the report to chart this workbook's results.
        </T>
      ) : (
        <T context="workbook report empty">No charts yet.</T>
      )}
    </div>
  );
}

/** The whole Report's settings: its colors and its filters. */
function WorkbookReportSettings({
  options,
  filters,
  data,
  changeOptions,
}: {
  options: WorkbookReportOptions | undefined;
  filters: ReportFilter[];
  data: ReportDataContextValue;
  changeOptions: (
    next: WorkbookReportOptions,
    label: string,
    group: string,
  ) => void;
}) {
  const gt = useGT();
  return (
    <>
      <ReportColorPaletteSelect
        paletteId={options?.paletteId}
        onChange={(paletteId) =>
          changeOptions(
            { ...options, paletteId },
            gt('Changed the colors', { context: 'report edit history step' }),
            'palette',
          )
        }
      />
      <ReportDataContext.Provider value={data}>
        <ReportFilterSettings
          filters={filters}
          onChange={(next) =>
            changeOptions(
              { ...options, filters: next },
              gt('Changed the filters', {
                context: 'report edit history step',
              }),
              'filters',
            )
          }
        />
      </ReportDataContext.Provider>
    </>
  );
}
